import React, { FormHTMLAttributes, ReactNode } from 'react';
import { useDensity } from '../../../hooks/useDensity';

export type FormLayout = 'vertical' | 'horizontal' | 'inline' | 'grid';
export type FieldSize = 'xs' | 'sm' | 'md' | 'lg';
export type LabelPosition = 'top' | 'left' | 'inline' | 'floating';

export interface CompactFormProps extends Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  children: ReactNode;
  layout?: FormLayout;
  columns?: number | 'auto';
  gap?: 'xs' | 'sm' | 'md' | 'lg';
  labelPosition?: LabelPosition;
  fieldSize?: FieldSize;
  maxWidth?: string;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void | Promise<void>;
  className?: string;
  compact?: boolean; // Force compact mode regardless of density
  responsive?: boolean; // Enable responsive behavior
}

const CompactForm: React.FC<CompactFormProps> = ({
  children,
  layout = 'vertical',
  columns = 'auto',
  gap = 'md',
  labelPosition = 'top',
  fieldSize = 'md',
  maxWidth,
  onSubmit,
  className = '',
  compact = false,
  responsive = true,
  ...formProps
}) => {
  const { classes, isCompact, isNormal } = useDensity();

  // Determine effective layout based on screen size and density
  const effectiveLayout = React.useMemo(() => {
    if (!responsive) return layout;

    const screenWidth = window.innerWidth;
    const shouldUseCompact = compact || isCompact || screenWidth < 768;

    if (shouldUseCompact && layout === 'horizontal') {
      return 'vertical';
    }
    if (shouldUseCompact && layout === 'grid') {
      return 'vertical';
    }

    return layout;
  }, [layout, responsive, compact, isCompact]);

  // Calculate columns for grid layout
  const effectiveColumns = React.useMemo(() => {
    if (effectiveLayout !== 'grid') return 1;

    if (columns === 'auto') {
      return isCompact ? 1 : isNormal ? 2 : 3;
    }

    // Respect density constraints
    const maxCols = isCompact ? 1 : isNormal ? 3 : 4;
    return Math.min(columns as number, maxCols);
  }, [effectiveLayout, columns, isCompact, isNormal]);

  // Generate density-aware spacing classes
  const spacingClasses = React.useMemo(() => {
    const gapMap = {
      xs: 'gap-1',
      sm: 'gap-2',
      md: 'gap-3',
      lg: 'gap-4',
    };

    const spaceMap = {
      xs: 'space-y-1',
      sm: 'space-y-2',
      md: 'space-y-3',
      lg: 'space-y-4',
    };

    return {
      gap: classes(gapMap[gap]),
      space: classes(spaceMap[gap]),
    };
  }, [gap, classes]);

  // Form container classes based on layout
  const formClasses = React.useMemo(() => {
    const baseClasses = 'w-full';

    switch (effectiveLayout) {
      case 'horizontal':
        return classes([baseClasses, spacingClasses.space, className].filter(Boolean).join(' '));
      case 'inline':
        return classes(
          [baseClasses, 'flex flex-wrap items-end', spacingClasses.gap, className]
            .filter(Boolean)
            .join(' ')
        );
      case 'grid':
        return classes(
          [
            baseClasses,
            'grid',
            spacingClasses.gap,
            `grid-cols-1`,
            effectiveColumns >= 2 ? 'sm:grid-cols-2' : '',
            effectiveColumns >= 3 ? 'md:grid-cols-3' : '',
            effectiveColumns >= 4 ? 'lg:grid-cols-4' : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')
        );
      case 'vertical':
      default:
        return classes([baseClasses, spacingClasses.space, className].filter(Boolean).join(' '));
    }
  }, [effectiveLayout, effectiveColumns, spacingClasses, classes, className]);

  // Container style for max width
  const containerStyle = React.useMemo(
    () => ({
      maxWidth: maxWidth || (isCompact ? 'none' : isNormal ? '768px' : '1024px'),
      ...(effectiveLayout === 'grid' &&
        effectiveColumns > 1 && {
          gridTemplateColumns: `repeat(${effectiveColumns}, minmax(0, 1fr))`,
        }),
    }),
    [maxWidth, isCompact, isNormal, effectiveLayout, effectiveColumns]
  );

  // Enhanced children with form context
  const enhancedChildren = React.useMemo(() => {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;

      // Add form context props to form field components
      const isFormField =
        child.type &&
        typeof child.type === 'function' &&
        (child.type.name?.includes('Field') ||
          child.type.name?.includes('Input') ||
          child.type.name?.includes('Select'));

      if (isFormField) {
        return React.cloneElement(child as React.ReactElement<any>, {
          layout: effectiveLayout,
          labelPosition,
          fieldSize,
          compact: compact || isCompact,
          ...child.props,
        });
      }

      return child;
    });
  }, [children, effectiveLayout, labelPosition, fieldSize, compact, isCompact]);

  return (
    <form {...formProps} className={formClasses} style={containerStyle} onSubmit={onSubmit}>
      {enhancedChildren}
    </form>
  );
};

// Field wrapper component for consistent spacing and layout
export interface FormFieldProps {
  children: ReactNode;
  label?: string;
  labelPosition?: LabelPosition;
  required?: boolean;
  error?: string;
  help?: string;
  className?: string;
  fieldSize?: FieldSize;
  fullWidth?: boolean;
}

export const FormField: React.FC<FormFieldProps> = ({
  children,
  label,
  labelPosition = 'top',
  required = false,
  error,
  help,
  className = '',
  fieldSize = 'md',
  fullWidth = true,
}) => {
  const { classes } = useDensity();

  const containerClasses = classes(
    [
      'form-field',
      fullWidth ? 'w-full' : 'w-auto',
      labelPosition === 'left' ? 'flex items-center space-x-3' : 'space-y-1',
      className,
    ]
      .filter(Boolean)
      .join(' ')
  );

  const getLabelSizeClass = () => {
    switch (fieldSize) {
      case 'xs':
        return 'text-xs';
      case 'sm':
        return 'text-sm';
      case 'md':
        return 'text-sm';
      case 'lg':
        return 'text-base';
      default:
        return 'text-sm';
    }
  };
  const labelClasses = classes(
    [
      'block font-medium text-gray-700',
      getLabelSizeClass(),
      labelPosition === 'left' ? 'min-w-0 flex-shrink-0' : '',
      labelPosition === 'inline' ? 'sr-only' : '',
    ]
      .filter(Boolean)
      .join(' ')
  );

  const fieldWrapperClasses = classes(labelPosition === 'left' ? 'flex-1' : 'w-full');

  const errorClasses = classes('text-red-600 text-xs mt-1');

  const helpClasses = classes('text-gray-500 text-xs mt-1');

  return (
    <div className={containerClasses}>
      {label && labelPosition !== 'floating' && (
        <label className={labelClasses}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className={fieldWrapperClasses}>
        {labelPosition === 'floating' ? (
          <div className="relative">
            {children}
            {label && (
              <label
                className={classes(
                  [labelClasses, 'absolute left-3 -top-2 bg-white px-1 text-xs transition-all']
                    .filter(Boolean)
                    .join(' ')
                )}
              >
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
          </div>
        ) : (
          children
        )}

        {error && <div className={errorClasses}>{error}</div>}
        {help && !error && <div className={helpClasses}>{help}</div>}
      </div>
    </div>
  );
};

// Preset configurations for common form types
export const FormPresets = {
  // Compact modal forms
  modal: {
    layout: 'vertical' as FormLayout,
    gap: 'sm' as const,
    labelPosition: 'top' as LabelPosition,
    fieldSize: 'sm' as FieldSize,
    maxWidth: '100%',
  },

  // Dashboard settings forms
  settings: {
    layout: 'grid' as FormLayout,
    columns: 2,
    gap: 'md' as const,
    labelPosition: 'top' as LabelPosition,
    fieldSize: 'md' as FieldSize,
  },

  // Search/filter forms
  search: {
    layout: 'inline' as FormLayout,
    gap: 'sm' as const,
    labelPosition: 'inline' as LabelPosition,
    fieldSize: 'sm' as FieldSize,
    compact: true,
  },

  // Detail/profile forms
  detail: {
    layout: 'horizontal' as FormLayout,
    gap: 'lg' as const,
    labelPosition: 'left' as LabelPosition,
    fieldSize: 'md' as FieldSize,
  },

  // Quick entry forms
  quick: {
    layout: 'vertical' as FormLayout,
    gap: 'xs' as const,
    labelPosition: 'floating' as LabelPosition,
    fieldSize: 'sm' as FieldSize,
    compact: true,
  },
};

export default CompactForm;
